import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { fmtUGX, shortDateTime } from "@/lib/format";
import { useState } from "react";
import { Clock, ChefHat, Truck, FileCheck2, Plus, CheckCircle2, AlertTriangle, Upload } from "lucide-react";
import { toast } from "sonner";

/* ============== SHIFTS ============== */
export function ShiftsPanel({ merchantId }: { merchantId: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(0);

  const { data: shifts = [] } = useQuery({
    queryKey: ["shifts", merchantId],
    queryFn: async () => (await (supabase.from as any)("merchant_shifts").select("*").eq("merchant_id", merchantId).order("opened_at", { ascending: false }).limit(50)).data ?? [],
  });

  const active = shifts.find((s: any) => s.status === "open");

  const openShift = useMutation({
    mutationFn: async () => {
      const { error } = await (supabase.from as any)("merchant_shifts").insert({ merchant_id: merchantId, opening_cash_ugx: open });
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Shift opened"); qc.invalidateQueries({ queryKey: ["shifts"] }); setOpen(0); },
  });

  const closeShift = useMutation({
    mutationFn: async ({ id, counted }: { id: string; counted: number }) => {
      // expected = opening + posted sales for shift duration (simplified: use opening)
      const expected = active.opening_cash_ugx;
      const variance = counted - expected;
      const { error } = await (supabase.from as any)("merchant_shifts").update({
        status: "closed", closed_at: new Date().toISOString(), counted_cash_ugx: counted, expected_cash_ugx: expected, variance_ugx: variance,
      }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Shift closed"); qc.invalidateQueries({ queryKey: ["shifts"] }); },
  });

  return (
    <div className="space-y-4">
      {!active ? (
        <Card className="p-5 shadow-card">
          <div className="font-display text-lg">Open a new shift</div>
          <p className="mt-1 text-sm text-muted-foreground">Record opening cash; cashier reconciliation happens at close.</p>
          <div className="mt-3 flex items-end gap-3">
            <div><Label className="text-xs">Opening cash (UGX)</Label><Input type="number" value={open} onChange={e => setOpen(Number(e.target.value))} /></div>
            <Button onClick={() => openShift.mutate()} disabled={openShift.isPending}><Clock className="mr-1 h-4 w-4" />Open shift</Button>
          </div>
        </Card>
      ) : (
        <ActiveShiftCard shift={active} onClose={(c) => closeShift.mutate({ id: active.id, counted: c })} />
      )}

      <Card>
        <div className="border-b p-4 font-display text-lg">Shift history</div>
        <div className="divide-y">
          {shifts.filter((s: any) => s.status === "closed").map((s: any) => (
            <div key={s.id} className="flex items-center justify-between p-3 text-sm">
              <div>
                <div className="font-medium">{shortDateTime(s.opened_at)} → {s.closed_at && shortDateTime(s.closed_at)}</div>
                <div className="text-xs text-muted-foreground">Expected {fmtUGX(s.expected_cash_ugx)} · Counted {fmtUGX(s.counted_cash_ugx ?? 0)}</div>
              </div>
              <Badge variant={Math.abs(s.variance_ugx ?? 0) > 1000 ? "destructive" : "secondary"}>
                {(s.variance_ugx ?? 0) >= 0 ? "+" : ""}{fmtUGX(s.variance_ugx ?? 0)}
              </Badge>
            </div>
          ))}
          {shifts.filter((s: any) => s.status === "closed").length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No closed shifts yet.</div>}
        </div>
      </Card>
    </div>
  );
}

function ActiveShiftCard({ shift, onClose }: { shift: any; onClose: (c: number) => void }) {
  const [counted, setCounted] = useState(shift.opening_cash_ugx);
  return (
    <Card className="border-primary/40 p-5 shadow-card">
      <div className="flex items-center justify-between">
        <div>
          <Badge className="bg-primary text-primary-foreground">Shift open</Badge>
          <div className="mt-1 font-display text-lg">Opened {shortDateTime(shift.opened_at)}</div>
          <div className="text-xs text-muted-foreground">Opening cash {fmtUGX(shift.opening_cash_ugx)}</div>
        </div>
        <div className="flex items-end gap-3">
          <div><Label className="text-xs">Counted cash now</Label><Input type="number" value={counted} onChange={e => setCounted(Number(e.target.value))} /></div>
          <Button variant="destructive" onClick={() => onClose(counted)}>Close & reconcile</Button>
        </div>
      </div>
    </Card>
  );
}

/* ============== KITCHEN ============== */
export function KitchenPanel({ merchantId }: { merchantId: string }) {
  const qc = useQueryClient();
  const [item, setItem] = useState(""); const [planned, setPlanned] = useState(0);

  const { data: jobs = [] } = useQuery({
    queryKey: ["kitchen", merchantId],
    queryFn: async () => (await (supabase.from as any)("kitchen_jobs").select("*").eq("merchant_id", merchantId).eq("for_date", new Date().toISOString().slice(0, 10)).order("created_at", { ascending: false })).data ?? [],
  });

  const add = useMutation({
    mutationFn: async () => {
      if (!item) throw new Error("Item required");
      const { error } = await (supabase.from as any)("kitchen_jobs").insert({ merchant_id: merchantId, item_name: item, qty_planned: planned });
      if (error) throw error;
    },
    onSuccess: () => { setItem(""); setPlanned(0); qc.invalidateQueries({ queryKey: ["kitchen"] }); },
  });

  const upd = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: any }) => {
      const { error } = await (supabase.from as any)("kitchen_jobs").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["kitchen"] }),
  });

  const totalWaste = jobs.reduce((a: number, j: any) => a + (j.qty_waste || 0), 0);

  return (
    <div className="space-y-4">
      <Card className="p-5 shadow-card">
        <div className="font-display text-lg">Today's kitchen plan</div>
        <div className="mt-3 grid grid-cols-12 items-end gap-3">
          <div className="col-span-6"><Label className="text-xs">Item</Label><Input value={item} onChange={e => setItem(e.target.value)} placeholder="Chapati, Beans plate…" /></div>
          <div className="col-span-3"><Label className="text-xs">Planned qty</Label><Input type="number" value={planned} onChange={e => setPlanned(Number(e.target.value))} /></div>
          <div className="col-span-3"><Button onClick={() => add.mutate()} className="w-full"><Plus className="mr-1 h-4 w-4" />Add</Button></div>
        </div>
      </Card>

      <Card>
        <div className="flex items-center justify-between border-b p-4">
          <div className="font-display text-lg">Production board · {jobs.length} items</div>
          <Badge variant={totalWaste > 10 ? "destructive" : "secondary"}>Waste today: {totalWaste}</Badge>
        </div>
        <div className="divide-y">
          {jobs.map((j: any) => (
            <div key={j.id} className="grid grid-cols-12 items-center gap-2 p-3 text-sm">
              <div className="col-span-3"><div className="font-medium">{j.item_name}</div><Badge variant="outline" className="mt-1 text-[10px] capitalize">{j.status}</Badge></div>
              <NumCol label="Planned" v={j.qty_planned} onChange={(v) => upd.mutate({ id: j.id, patch: { qty_planned: v } })} />
              <NumCol label="Produced" v={j.qty_produced} onChange={(v) => upd.mutate({ id: j.id, patch: { qty_produced: v } })} />
              <NumCol label="Sold" v={j.qty_sold} onChange={(v) => upd.mutate({ id: j.id, patch: { qty_sold: v } })} />
              <NumCol label="Waste" v={j.qty_waste} onChange={(v) => upd.mutate({ id: j.id, patch: { qty_waste: v } })} />
              <div className="col-span-1">
                <Select value={j.status} onValueChange={(s) => upd.mutate({ id: j.id, patch: { status: s } })}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prep">Prep</SelectItem>
                    <SelectItem value="cooking">Cooking</SelectItem>
                    <SelectItem value="ready">Ready</SelectItem>
                    <SelectItem value="done">Done</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
          {jobs.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No kitchen jobs queued for today.</div>}
        </div>
      </Card>
    </div>
  );
}

function NumCol({ label, v, onChange }: { label: string; v: number; onChange: (n: number) => void }) {
  return (
    <div className="col-span-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <Input type="number" defaultValue={v} className="h-8" onBlur={(e) => onChange(Number(e.currentTarget.value))} />
    </div>
  );
}

/* ============== DELIVERIES ============== */
export function DeliveriesPanel({ merchantId }: { merchantId: string }) {
  const qc = useQueryClient();
  const [rider, setRider] = useState(""); const [dest, setDest] = useState(""); const [win, setWin] = useState("Lunch window 12:30–13:30");

  const { data: deliveries = [] } = useQuery({
    queryKey: ["deliveries", merchantId],
    queryFn: async () => (await (supabase.from as any)("deliveries").select("*").eq("merchant_id", merchantId).order("created_at", { ascending: false }).limit(50)).data ?? [],
  });

  const create = useMutation({
    mutationFn: async () => {
      const { error } = await (supabase.from as any)("deliveries").insert({ merchant_id: merchantId, rider_name: rider || null, destination: dest || null, window_label: win });
      if (error) throw error;
    },
    onSuccess: () => { setRider(""); setDest(""); qc.invalidateQueries({ queryKey: ["deliveries"] }); },
  });

  const mark = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const patch: any = { status };
      if (status === "delivered") patch.delivered_at = new Date().toISOString();
      const { error } = await (supabase.from as any)("deliveries").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deliveries"] }),
  });

  const byStatus = (s: string) => deliveries.filter((d: any) => d.status === s);

  return (
    <div className="space-y-4">
      <Card className="p-5 shadow-card">
        <div className="font-display text-lg">Schedule a delivery</div>
        <div className="mt-3 grid grid-cols-12 items-end gap-3">
          <div className="col-span-3"><Label className="text-xs">Rider</Label><Input value={rider} onChange={e => setRider(e.target.value)} placeholder="John K." /></div>
          <div className="col-span-4"><Label className="text-xs">Destination / school gate</Label><Input value={dest} onChange={e => setDest(e.target.value)} placeholder="Greenhill Academy main gate" /></div>
          <div className="col-span-3"><Label className="text-xs">Window</Label><Input value={win} onChange={e => setWin(e.target.value)} /></div>
          <div className="col-span-2"><Button onClick={() => create.mutate()} className="w-full"><Truck className="mr-1 h-4 w-4" />Add</Button></div>
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-4">
        {["queued", "in_transit", "delivered", "cancelled"].map(col => (
          <Card key={col}>
            <div className="border-b p-3 text-xs font-semibold uppercase tracking-wider">{col.replace("_", " ")} · {byStatus(col).length}</div>
            <div className="max-h-[480px] space-y-2 overflow-auto p-3">
              {byStatus(col).map((d: any) => (
                <div key={d.id} className="rounded-lg border p-3 text-xs">
                  <div className="font-semibold">{d.destination ?? "—"}</div>
                  <div className="text-muted-foreground">{d.rider_name ?? "Unassigned"} · {d.window_label}</div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {col !== "in_transit" && col !== "delivered" && <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => mark.mutate({ id: d.id, status: "in_transit" })}>Dispatch</Button>}
                    {col !== "delivered" && <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => mark.mutate({ id: d.id, status: "delivered" })}><CheckCircle2 className="mr-1 h-3 w-3" />Delivered</Button>}
                    {col !== "cancelled" && col !== "delivered" && <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => mark.mutate({ id: d.id, status: "cancelled" })}>Cancel</Button>}
                  </div>
                </div>
              ))}
              {byStatus(col).length === 0 && <div className="text-center text-[10px] text-muted-foreground">Empty</div>}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ============== COMPLIANCE DOCS ============== */
export function ComplianceDocsPanel({ merchantId }: { merchantId: string }) {
  const qc = useQueryClient();
  const [docType, setDocType] = useState("trading_license"); const [docNo, setDocNo] = useState("");

  const { data: docs = [] } = useQuery({
    queryKey: ["m-docs", merchantId],
    queryFn: async () => (await (supabase.from as any)("merchant_documents").select("*").eq("merchant_id", merchantId).order("submitted_at", { ascending: false })).data ?? [],
  });

  const submit = useMutation({
    mutationFn: async () => {
      const { error } = await (supabase.from as any)("merchant_documents").insert({ merchant_id: merchantId, doc_type: docType, doc_number: docNo || null });
      if (error) throw error;
    },
    onSuccess: () => { setDocNo(""); toast.success("Document submitted for review"); qc.invalidateQueries({ queryKey: ["m-docs"] }); },
  });

  const required = ["trading_license", "ura_tin", "food_handlers_permit", "school_approval"];
  const have = new Set(docs.map((d: any) => d.doc_type));

  return (
    <div className="space-y-4">
      <Card className="p-5 shadow-card">
        <div className="flex items-center gap-2"><FileCheck2 className="h-4 w-4 text-primary" /><div className="font-display text-lg">Compliance checklist</div></div>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {required.map(r => (
            <div key={r} className="flex items-center justify-between rounded-lg border p-3 text-sm">
              <span className="capitalize">{r.replace(/_/g, " ")}</span>
              {have.has(r) ? <Badge variant="secondary"><CheckCircle2 className="mr-1 h-3 w-3" />On file</Badge>
                : <Badge variant="destructive"><AlertTriangle className="mr-1 h-3 w-3" />Missing</Badge>}
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5 shadow-card">
        <div className="font-display text-lg">Submit a document</div>
        <div className="mt-3 grid grid-cols-12 items-end gap-3">
          <div className="col-span-4">
            <Label className="text-xs">Document type</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="trading_license">Trading license</SelectItem>
                <SelectItem value="ura_tin">URA TIN</SelectItem>
                <SelectItem value="food_handlers_permit">Food handlers permit</SelectItem>
                <SelectItem value="school_approval">School approval letter</SelectItem>
                <SelectItem value="kcca_health">KCCA health cert.</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-5"><Label className="text-xs">Reference number</Label><Input value={docNo} onChange={e => setDocNo(e.target.value)} placeholder="TIN / license number" /></div>
          <div className="col-span-3"><Button onClick={() => submit.mutate()} className="w-full"><Upload className="mr-1 h-4 w-4" />Submit</Button></div>
        </div>
      </Card>

      <Card>
        <div className="border-b p-4 font-display text-lg">Submission history</div>
        <div className="divide-y">
          {docs.map((d: any) => (
            <div key={d.id} className="flex items-center justify-between p-3 text-sm">
              <div>
                <div className="font-medium capitalize">{d.doc_type.replace(/_/g, " ")}</div>
                <div className="text-xs text-muted-foreground">{d.doc_number ?? "—"} · {shortDateTime(d.submitted_at)}</div>
              </div>
              <Badge variant={d.status === "approved" ? "secondary" : d.status === "rejected" ? "destructive" : "outline"} className="capitalize">{d.status}</Badge>
            </div>
          ))}
          {docs.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">Nothing submitted yet.</div>}
        </div>
      </Card>
    </div>
  );
}
